import React, { useState, useMemo, useRef, useEffect } from 'react';

import { Input } from 'antd';

import styled from 'styled-components';

import { getMoment } from '@libs/NwMoment';
import { getNow } from '@libs/NwMoment';

import { useUserPermission } from '@contexts/UserContext';

import { forceReloadInvoices, useInvoices } from '@hooks/API/UseInvoices';

import InvoiceItem from './InvoiceItem';
import { StatementContainer } from '../StatementUI';
import EmptyState from '@components/Gui/EmptyState';
import { Loading } from '@components/Gui/Loading';
import NwIcon from '@components/Gui/NwIcon';
import PeriodSelector from '@components/Gui/PeriodSelector';
import InvoiceDrawer from '@components/InvoicesAndCreditNotes/InvoiceDrawer';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

/**
 * InvoicesWrapper
 *
 * Universal wrapper for invoices tab.
 * Reusable across all contact types.
 */

const Wrapper = styled.div``;

const searchInText = (text, searchTerm) => {
    if (text && text.toLowerCase().includes(searchTerm.toLowerCase())) {
        return true;
    }
    return false;
};

const InvoicesWrapper = ({ contactID, onUpdateTransactions }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [searchMode, setSearchMode] = useState(false);
    const [range, setRange] = useState([getNow().startOf('year'), getNow().endOf('year')]);
    const [selectedInvoice, setSelectedInvoice] = useState();
    const searchRef = useRef(null);
    const accessAccounting = useUserPermission('AccessAccounting');
    const { data: invoices, isFetching } = useInvoices(contactID);

    const handleChangeSearchTerm = (event) => {
        setSearchTerm(event.target.value);
    };

    const handleClearSearchTerm = () => {
        setSearchMode(false);
        setSearchTerm('');
    };

    const handleInvoiceClick = (invoiceID) => {
        if (accessAccounting) {
            setSelectedInvoice(invoiceID);
        }
    };

    const handleCloseInvoiceDrawer = () => {
        forceReloadInvoices(contactID);
        onUpdateTransactions();
        setSelectedInvoice(null);
    };

    useEffect(() => {
        if (searchMode) {
            searchRef.current.focus();
        }
    }, [searchMode]);

    const filterInvoices = (invoicestosearch, searchTerm) => {
        return invoicestosearch.filter((invoice) => {
            if (
                searchInText(invoice.Name, searchTerm) ||
                searchInText(invoice.Number, searchTerm) ||
                searchInText(invoice.HeaderNote, searchTerm) ||
                searchInText(invoice.FooterNote, searchTerm) ||
                searchInText(invoice.AddressNote, searchTerm)
            ) {
                return true;
            }
            return false;
        });
    };

    const activeInvoices = useMemo(() => {
        if (invoices && invoices.length > 0) {
            let act_invoices = invoices.filter((invoice) => {
                return !invoice.Date || getMoment(invoice.Date).isBetween(range[0], range[1], null, '[]');
            });
            if (searchTerm) {
                act_invoices = filterInvoices(act_invoices, searchTerm);
            }
            return act_invoices;
        }
        return [];
    }, [invoices, range, searchTerm]);

    return (
        <StatementContainer>
            <div className="statement-header">
                <div className="statement-navigation">
                    <PeriodSelector period={range} onChange={setRange} alwaysEnabled defaultOption="year" />
                    <div className="statement-search">
                        <NwIcon icon={byPrefixAndName.fasl['search']} />
                        <Input
                            autoComplete="off"
                            value={searchTerm}
                            placeholder="Search invoice (title, number or note)"
                            onChange={handleChangeSearchTerm}
                            allowClear={{
                                clearIcon: (
                                    <NwIcon
                                        icon={byPrefixAndName.fass['circle-xmark']}
                                        onClick={handleClearSearchTerm}
                                    />
                                ),
                            }}
                        />
                    </div>
                </div>
            </div>
            {isFetching ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem 0' }}>
                    <Loading text="" textBlack />
                </div>
            ) : activeInvoices && activeInvoices.length > 0 ? (
                <Wrapper>
                    {activeInvoices.map((invoice) => (
                        <InvoiceItem
                            key={invoice.ID}
                            invoice={invoice}
                            onInvoiceClick={handleInvoiceClick}
                            hasAccountingAccess={accessAccounting}
                        />
                    ))}
                    {selectedInvoice && (
                        <InvoiceDrawer invoiceId={selectedInvoice} onClose={handleCloseInvoiceDrawer} />
                    )}
                </Wrapper>
            ) : (
                <Wrapper>
                    <EmptyState message="No invoices" />
                </Wrapper>
            )}
        </StatementContainer>
    );
};

export default InvoicesWrapper;

